import { whenThirdPartyAllowed } from '@/lib/defer-third-party'
import type { GptSize, GptSizeMapEntry } from '@/lib/ad-slots'

type GptSlot = {
  getSlotElementId: () => string
  getAdUnitPath?: () => string
  defineSizeMapping?: (mapping: unknown) => unknown
  addService: (s: unknown) => unknown
}

type SlotRenderEvent = {
  slot: GptSlot & { isEmpty?: () => boolean }
  isEmpty?: boolean
  size?: [number, number] | null
  /** rewardedSlotReady eventinde reklamı görünür yapar */
  makeRewardedVisible?: () => boolean | void
  /** rewardedSlotGranted eventinde ödül bilgisi */
  payload?: { type: string; amount: number } | null
}

type SizeMappingBuilder = {
  addSize: (
    viewport: [number, number],
    sizes: GptSize | GptSize[],
  ) => SizeMappingBuilder
  build: () => unknown
}

declare global {
  interface Window {
    googletag?: {
      cmd: Array<() => void>
      apiReady?: boolean
      defineSlot?: (
        path: string,
        size: GptSize | GptSize[],
        id: string,
      ) => GptSlot | null
      defineOutOfPageSlot?: (
        path: string,
        divOrFormat: string | number,
      ) => GptSlot | null
      enums?: {
        OutOfPageFormat?: {
          REWARDED?: number
          INTERSTITIAL?: number
          TOP_ANCHOR?: number
          BOTTOM_ANCHOR?: number
        }
      }
      sizeMapping?: () => SizeMappingBuilder
      destroySlots?: (slots?: unknown[]) => void
      setConfig?: (config: {
        singleRequest?: boolean
        safeFrame?: { forceSafeFrame?: boolean }
        adsenseAttributes?: {
          page_url?: string | null
          document_language?: string | null
        } | null
      }) => void
      pubads?: () => {
        enableSingleRequest?: () => void
        collapseEmptyDivs: (
          collapse?: boolean,
          collapseBeforeFetch?: boolean,
        ) => void
        setForceSafeFrame?: (force: boolean) => void
        set?: (key: string, value: string) => void
        refresh?: (slots?: unknown[]) => void
        addEventListener?: (
          event: string,
          cb: (event: SlotRenderEvent) => void,
        ) => void
        removeEventListener?: (
          event: string,
          cb: (event: SlotRenderEvent) => void,
        ) => void
        getSlots?: () => GptSlot[]
      }
      enableServices?: () => void
      display?: (idOrSlot: string | GptSlot) => void
    }
  }
}

type ScriptLoad = {
  promise: Promise<void>
  cancel: () => void
  users: number
  settled: boolean
}
let scriptLoad: ScriptLoad | null = null
const activeRewardedSlots = new Set<GptSlot>()
const knownRewardedSlots = new WeakSet<GptSlot>()
let rewardedRequestActive = false
const GPT_SCRIPT_TIMEOUT_MS = 10_000
const REWARDED_PLAYBACK_TIMEOUT_MS = 180_000

class GptLoadError extends Error {
  constructor(readonly reason: string) {
    super(reason)
  }
}
let pubadsConfigured = false
let gptServicesEnabled = false
let renderListenerAttached = false

const definedDivIds = new Set<string>()
const slotByDivId = new Map<string, unknown>()
const renderWaiters = new Map<string, Set<(empty: boolean) => void>>()

function notifyRender(divId: string, empty: boolean) {
  const waiters = renderWaiters.get(divId)
  if (!waiters?.size) return
  renderWaiters.delete(divId)
  for (const cb of waiters) cb(empty)
}

function attachRenderListener() {
  if (renderListenerAttached) return
  const gt = window.googletag
  const pubads = gt?.pubads?.()
  if (!pubads?.addEventListener) return
  pubads.addEventListener('slotRenderEnded', (event) => {
    const divId = event.slot.getSlotElementId()
    const empty =
      event.isEmpty ??
      event.slot.isEmpty?.() ??
      !(event.size && event.size[0] > 0 && event.size[1] > 0)
    notifyRender(divId, Boolean(empty))
  })
  renderListenerAttached = true
}

/**
 * GPT/AdSense yüklemeden önce: lab UA’da hiç açılmaz;
 * gerçek kullanıcıda ilk etkileşim veya load+fallback.
 */
export async function waitForAdIdle(minDelayMs = 0): Promise<void> {
  if (typeof window === 'undefined') return
  await whenThirdPartyAllowed({
    fallbackMs: Math.max(3_500, minDelayMs || 0),
  })
}

function isGptReady(): boolean {
  return Boolean(window.googletag?.apiReady || window.googletag?.defineSlot)
}

function startGptScriptLoad(): ScriptLoad {
  const load: ScriptLoad = {
    promise: Promise.resolve(),
    cancel: () => {},
    users: 0,
    settled: false,
  }
  load.promise = new Promise<void>((resolve, reject) => {
    let script: HTMLScriptElement | null = null
    let readyCheck: (() => void) | null = null
    const cleanup = () => {
      window.clearTimeout(timer)
      script?.removeEventListener('load', onLoad)
      script?.removeEventListener('error', onError)
      const commands = window.googletag?.cmd
      if (readyCheck && Array.isArray(commands)) {
        const index = commands.indexOf(readyCheck)
        if (index >= 0) commands.splice(index, 1)
      }
    }
    const finish = (reason?: string) => {
      if (load.settled) return
      load.settled = true
      cleanup()
      if (reason && !isGptReady()) script?.remove()
      if (scriptLoad === load) scriptLoad = null
      if (reason) reject(new GptLoadError(reason))
      else resolve()
    }
    const checkReady = () => {
      if (load.settled) return
      if (isGptReady()) {
        finish()
        return
      }
      if (readyCheck) return
      readyCheck = () => {
        if (load.settled) return
        finish(isGptReady() ? undefined : 'gpt_not_ready')
      }
      window.googletag = window.googletag || { cmd: [] }
      window.googletag.cmd.push(readyCheck)
    }
    const onLoad = () => {
      if (script) script.dataset.loaded = '1'
      checkReady()
    }
    const onError = () => finish('script_error')
    const timer = window.setTimeout(
      () => finish('script_timeout'),
      GPT_SCRIPT_TIMEOUT_MS,
    )
    load.cancel = () => finish('aborted')
    void waitForAdIdle(400)
      .then(() => {
        if (load.settled) return
        if (isGptReady()) {
          finish()
          return
        }
        window.googletag = window.googletag || { cmd: [] }
        script = document.getElementById('gpt-js') as HTMLScriptElement | null
        const existing = Boolean(script)
        if (!script) {
          script = document.createElement('script')
          script.id = 'gpt-js'
          script.async = true
          script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'
          const nonce = document
            .querySelector('script[nonce]')
            ?.getAttribute('nonce')
          if (nonce) script.setAttribute('nonce', nonce)
        }
        script.addEventListener('load', onLoad)
        script.addEventListener('error', onError)
        if (script.dataset.loaded === '1') checkReady()
        if (!existing) document.head.appendChild(script)
      })
      .catch(() => finish('script_error'))
  })
  return load
}

/** Failed loads are discarded; concurrent callers share a cancellable attempt. */
export function ensureGptScript(options?: {
  signal?: AbortSignal
}): Promise<void> {
  if (typeof window === 'undefined' || isGptReady()) return Promise.resolve()
  if (options?.signal?.aborted)
    return Promise.reject(new GptLoadError('aborted'))
  const load = scriptLoad || (scriptLoad = startGptScriptLoad())
  load.users += 1
  return new Promise<void>((resolve, reject) => {
    let settled = false
    const finish = (error?: unknown) => {
      if (settled) return
      settled = true
      options?.signal?.removeEventListener('abort', onAbort)
      load.users -= 1
      if (!load.users && !load.settled) load.cancel()
      if (error) reject(error)
      else resolve()
    }
    const onAbort = () => finish(new GptLoadError('aborted'))
    options?.signal?.addEventListener('abort', onAbort, { once: true })
    load.promise.then(() => finish(), finish)
  })
}

function resolveAdsPageUrl(): string | null {
  if (typeof window === 'undefined') return null
  const host = window.location.hostname.toLowerCase()
  // beta/localhost: GAM inventory çoğu zaman prod domaine bağlı
  if (
    host === 'beta.haber.com' ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.local')
  ) {
    const origin =
      (typeof process !== 'undefined' &&
        process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, '')) ||
      window.location.origin
    return `${origin}${window.location.pathname}${window.location.search}`
  }
  return null
}

function ensurePubadsServices() {
  const gt = window.googletag
  if (!gt?.pubads || pubadsConfigured) return
  const pageUrl = resolveAdsPageUrl()
  try {
    if (pageUrl) {
      gt.setConfig?.({
        adsenseAttributes: { page_url: pageUrl },
      })
      gt.pubads().set?.('page_url', pageUrl)
    }
  } catch {
    /* ignore */
  }
  // www.haber.com ile aynı: SRA açık. collapseEmptyDivs YOK —
  // boş fill'de display:none + 0×0 shell fill retry'ı öldürüyordu.
  try {
    // GPT: enableSingleRequest() deprecated → setConfig({ singleRequest })
    if (typeof gt.setConfig === 'function') {
      gt.setConfig({ singleRequest: true })
    } else {
      gt.pubads().enableSingleRequest?.()
    }
  } catch {
    /* ignore */
  }
  attachRenderListener()
  pubadsConfigured = true
}

export function waitForGamSlotRender(
  divId: string,
  timeoutMs = 12000,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false)
      return
    }

    let settled = false
    const finish = (empty: boolean) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      renderWaiters.delete(divId)
      resolve(!empty)
    }

    const timer = window.setTimeout(() => {
      // Timeout: slotRenderEnded gelmediyse boş say — boş yer kaplamasın
      finish(true)
    }, timeoutMs)

    const set = renderWaiters.get(divId) ?? new Set()
    set.add(finish)
    renderWaiters.set(divId, set)
  })
}

function applySizeMapping(
  slot: GptSlot,
  mapping: GptSizeMapEntry[] | undefined,
) {
  if (!mapping?.length) return
  const gt = window.googletag
  if (!gt?.sizeMapping || !slot.defineSizeMapping) return
  try {
    let builder = gt.sizeMapping()
    for (const row of mapping) {
      builder = builder.addSize([row.minWidth, 0], row.sizes)
    }
    slot.defineSizeMapping(builder.build())
  } catch {
    /* sizeMapping desteklenmiyorsa yoksay */
  }
}

export function displayGamSlot(
  divId: string,
  adUnitPath: string,
  sizes: GptSize[],
  sizeMapping?: GptSizeMapEntry[],
): void {
  if (typeof window === 'undefined') return
  if (!adUnitPath || !divId) return

  window.googletag = window.googletag || { cmd: [] }
  window.googletag.cmd.push(() => {
    try {
      const gt = window.googletag
      if (!gt?.defineSlot || !gt.pubads || !gt.enableServices || !gt.display)
        return

      ensurePubadsServices()

      if (!definedDivIds.has(divId)) {
        const defined = gt.defineSlot(adUnitPath, sizes, divId)
        if (!defined) return
        applySizeMapping(defined, sizeMapping)
        defined.addService(gt.pubads())
        definedDivIds.add(divId)
        slotByDivId.set(divId, defined)

        const isLateSlot = gptServicesEnabled
        if (!gptServicesEnabled) {
          gt.enableServices()
          gptServicesEnabled = true
        }

        gt.display(divId)

        if (isLateSlot && gt.pubads()?.refresh) {
          gt.pubads().refresh!([defined])
        }
        return
      }

      if (!gptServicesEnabled) {
        gt.enableServices()
        gptServicesEnabled = true
      }

      gt.display(divId)
      const existing = slotByDivId.get(divId)
      if (existing && gt.pubads()?.refresh) {
        gt.pubads().refresh!([existing])
      }
    } catch {
      /* GPT hatası — sessiz */
    }
  })
}

export async function bootstrapGamSlot(
  divId: string,
  adUnitPath: string,
  sizes: GptSize[],
  sizeMapping?: GptSizeMapEntry[],
): Promise<boolean> {
  try {
    await ensureGptScript()
  } catch {
    return false
  }
  // Waiter ÖNCE — display slotRenderEnded’i kaçırmasın
  const rendered = waitForGamSlotRender(divId)
  displayGamSlot(divId, adUnitPath, sizes, sizeMapping)
  return rendered
}

export function refreshGamSlot(divId: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false)
      return
    }

    window.googletag = window.googletag || { cmd: [] }
    window.googletag.cmd.push(() => {
      try {
        const gt = window.googletag
        const slot = slotByDivId.get(divId)
        const refresh = gt?.pubads?.()?.refresh
        if (!slot || !refresh) {
          resolve(false)
          return
        }

        const rendered = waitForGamSlotRender(divId)
        refresh([slot])
        void rendered.then(resolve)
      } catch {
        resolve(false)
      }
    })
  })
}

export type RewardedAdResult = 'granted' | 'closed' | 'unfilled' | 'error'

/**
 * Resmî GPT rewarded (ödüllü) reklam akışı — Offerwall benzeri kapılar için.
 * https://developers.google.com/publisher-tag/samples/display-rewarded-ad
 *
 * - `rewardedSlotReady` → reklam hazır, görünür yapılır
 * - `rewardedSlotGranted` → kullanıcı ödülü kazandı → 'granted'
 * - `rewardedSlotClosed` (ödülsüz) → 'closed'
 * - Doluluk yok / zaman aşımı (ad blocker dahil) → 'unfilled'
 *
 * GPT scripti tek sefer yüklenir (ensureGptScript); SSR'da çağrılırsa 'error'.
 */
export type RewardedAdOptions = {
  signal?: AbortSignal
  onDiagnostic?: (reason: string) => void
}

export function showRewardedAd(
  adUnitPath: string,
  timeoutMs = 20_000,
  options: RewardedAdOptions = {},
): Promise<RewardedAdResult> {
  const diagnostic = (reason: string) => {
    try {
      options.onDiagnostic?.(reason)
    } catch {
      /* observer must not affect ads */
    }
  }
  if (typeof window === 'undefined' || !adUnitPath) {
    diagnostic('error')
    return Promise.resolve('error')
  }
  if (options.signal?.aborted) {
    diagnostic('aborted')
    return Promise.resolve('error')
  }
  if (rewardedRequestActive) {
    diagnostic('busy')
    return Promise.resolve('closed')
  }
  rewardedRequestActive = true
  return new Promise<RewardedAdResult>((resolve) => {
    let settled = false
    let granted = false
    let phase: 'script' | 'queue' | 'ad' | 'visible' = 'script'
    let slot: GptSlot | null = null
    let tag: Window['googletag']
    let pubads:
      | ReturnType<NonNullable<NonNullable<Window['googletag']>['pubads']>>
      | undefined
    let queued: (() => void) | null = null
    let timer = 0
    const loading = new AbortController()
    const listeners: Array<[string, (event: SlotRenderEvent) => void]> = []
    const cleanup = () => {
      window.clearTimeout(timer)
      options.signal?.removeEventListener('abort', onAbort)
      loading.abort()
      if (queued && Array.isArray(tag?.cmd)) {
        const index = tag.cmd.indexOf(queued)
        if (index >= 0) tag.cmd.splice(index, 1)
      }
      for (const [event, handler] of listeners) {
        try {
          pubads?.removeEventListener?.(event, handler)
        } catch {
          /* continue cleanup */
        }
      }
      if (slot) {
        activeRewardedSlots.delete(slot)
        try {
          tag?.destroySlots?.([slot])
        } catch {
          /* continue cleanup */
        }
      }
      rewardedRequestActive = false
    }
    const settle = (result: RewardedAdResult, reason: string) => {
      if (settled) return
      settled = true
      cleanup()
      diagnostic(reason)
      resolve(result)
    }
    const onAbort = () => settle('error', 'aborted')
    const expire = () =>
      settle(
        granted ? 'granted' : 'unfilled',
        phase === 'script'
          ? 'script_timeout'
          : phase === 'queue'
            ? 'gpt_not_ready'
            : 'timeout',
      )
    const duration = Math.min(
      60_000,
      Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : 20_000,
    )
    timer = window.setTimeout(expire, duration)
    options.signal?.addEventListener('abort', onAbort, { once: true })

    void ensureGptScript({ signal: loading.signal })
      .then(() => {
        if (settled) return
        phase = 'queue'
        tag = window.googletag = window.googletag || { cmd: [] }
        queued = () => {
          if (settled) return
          try {
            const gt = tag
            const format = gt?.enums?.OutOfPageFormat?.REWARDED
            if (
              !gt?.defineOutOfPageSlot ||
              format == null ||
              !gt.pubads ||
              !gt.display
            ) {
              settle('error', 'unsupported')
              return
            }
            pubads = gt.pubads()
            if (!pubads.addEventListener || !pubads.removeEventListener) {
              settle('error', 'gpt_not_ready')
              return
            }
            ensurePubadsServices()
            slot = gt.defineOutOfPageSlot(adUnitPath, format)
            if (!slot) {
              settle('error', 'unsupported')
              return
            }
            activeRewardedSlots.add(slot)
            knownRewardedSlots.add(slot)
            slot.addService(pubads)
            const own = (event: SlotRenderEvent) =>
              !settled && event.slot === slot
            const onReady = (event: SlotRenderEvent) => {
              if (!own(event)) return
              if (phase === 'visible') return
              if (!event.makeRewardedVisible) {
                settle('error', 'unsupported')
                return
              }
              phase = 'visible'
              window.clearTimeout(timer)
              timer = window.setTimeout(expire, REWARDED_PLAYBACK_TIMEOUT_MS)
              diagnostic('ready')
              try {
                if (event.makeRewardedVisible() === false)
                  settle('error', 'unsupported')
              } catch {
                settle('error', 'error')
              }
            }
            const onGranted = (event: SlotRenderEvent) => {
              if (!own(event)) return
              granted = true
              diagnostic('granted')
            }
            const onClosed = (event: SlotRenderEvent) => {
              if (own(event))
                settle(
                  granted ? 'granted' : 'closed',
                  granted ? 'granted' : 'closed',
                )
            }
            const onRender = (event: SlotRenderEvent) => {
              if (
                own(event) &&
                (event.isEmpty ?? event.slot.isEmpty?.() ?? false)
              )
                settle('unfilled', 'empty')
            }
            listeners.push(
              ['rewardedSlotReady', onReady],
              ['rewardedSlotGranted', onGranted],
              ['rewardedSlotClosed', onClosed],
              ['slotRenderEnded', onRender],
            )
            for (const [event, handler] of listeners)
              pubads.addEventListener(event, handler)
            phase = 'ad'
            if (!gptServicesEnabled) {
              if (!gt.enableServices) {
                settle('error', 'gpt_not_ready')
                return
              }
              gt.enableServices()
              gptServicesEnabled = true
            }
            gt.display(slot)
          } catch {
            settle('error', 'error')
          }
        }
        tag.cmd.push(queued)
      })
      .catch((error: unknown) => {
        if (settled) return
        settle('error', error instanceof GptLoadError ? error.reason : 'error')
      })
  })
}

/** haber.com — tüm GAM slotlarını yenile */
export function refreshAllGamAds(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve()
      return
    }
    window.googletag = window.googletag || { cmd: [] }
    window.googletag.cmd.push(() => {
      try {
        const pubads = window.googletag?.pubads?.()
        if (pubads?.getSlots) {
          const slots = pubads
            .getSlots()
            .filter((slot) => !knownRewardedSlots.has(slot))
          if (slots.length) pubads.refresh?.(slots)
        } else if (!activeRewardedSlots.size) {
          pubads?.refresh?.()
        }
      } catch {
        /* ignore */
      }
      resolve()
    })
  })
}
